import { useState, useEffect, useMemo } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { api } from '../lib/api'
import type { Company, CategorySlug } from '../lib/types'
import { CATEGORY_LABELS, CATEGORY_BADGE_CLASS } from '../lib/types'

export default function Companies() {
  const [companies, setCompanies] = useState<Company[]>([])
  const [loading, setLoading] = useState(true)
  const [searchParams, setSearchParams] = useSearchParams()
  const [search, setSearch] = useState(searchParams.get('search') || '')
  const activeCategory = searchParams.get('category') || ''

  useEffect(() => {
    api.listCompanies().then(setCompanies).catch(console.error).finally(() => setLoading(false))
  }, [])

  const filtered = useMemo(() => {
    let result = companies
    if (activeCategory) {
      result = result.filter(c => c.categories.includes(activeCategory))
    }
    if (search.trim()) {
      const q = search.toLowerCase()
      result = result.filter(c =>
        c.name.toLowerCase().includes(q) ||
        c.description?.toLowerCase().includes(q) ||
        c.products?.toLowerCase().includes(q) ||
        c.booth?.toLowerCase().includes(q)
      )
    }
    return result.sort((a, b) => a.name.localeCompare(b.name))
  }, [companies, activeCategory, search])

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    companies.forEach(c => c.categories.forEach(cat => { counts[cat] = (counts[cat] || 0) + 1 }))
    return counts
  }, [companies])

  const setCategory = (cat: string) => {
    const p = new URLSearchParams(searchParams)
    if (cat) p.set('category', cat); else p.delete('category')
    setSearchParams(p)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="flex-1">
          <input
            type="search"
            placeholder="Search exhibitors by name, product, or booth..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="input text-lg"
          />
        </div>
        <div className="text-sm text-gray-500 self-center whitespace-nowrap">
          {filtered.length} of {companies.length} exhibitors
        </div>
      </div>

      <div className="flex gap-2 flex-wrap mb-6">
        <button
          onClick={() => setCategory('')}
          className={`badge px-3 py-1.5 cursor-pointer transition-all ${!activeCategory ? 'bg-brand-600 text-white ring-2 ring-brand-300' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
        >
          All ({companies.length})
        </button>
        {(Object.keys(CATEGORY_LABELS) as CategorySlug[]).map(slug => (
          categoryCounts[slug] ? (
            <button
              key={slug}
              onClick={() => setCategory(activeCategory === slug ? '' : slug)}
              className={`badge px-3 py-1.5 cursor-pointer transition-all ${
                activeCategory === slug
                  ? `${CATEGORY_BADGE_CLASS[slug]} ring-2 ring-offset-1 ring-current`
                  : `${CATEGORY_BADGE_CLASS[slug]} opacity-70 hover:opacity-100`
              }`}
            >
              {CATEGORY_LABELS[slug]} ({categoryCounts[slug]})
            </button>
          ) : null
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p className="text-lg">No exhibitors match your search</p>
          <button onClick={() => { setSearch(''); setCategory('') }} className="text-brand-600 mt-2 hover:underline">
            Clear filters
          </button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map(company => (
            <Link key={company.id} to={`/companies/${company.id}`} className="card card-hover group">
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-semibold text-lg group-hover:text-brand-600 transition-colors">
                  {company.name}
                </h3>
                {company.booth && (
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-md font-mono whitespace-nowrap">
                    {company.booth}
                  </span>
                )}
              </div>
              {company.description && (
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">{company.description}</p>
              )}
              <div className="flex gap-1 flex-wrap">
                {company.categories.map(cat => (
                  <span key={cat} className={`badge text-xs ${CATEGORY_BADGE_CLASS[cat as CategorySlug] || 'badge-other'}`}>
                    {CATEGORY_LABELS[cat as CategorySlug] || cat}
                  </span>
                ))}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
